import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useQuery } from '@tanstack/react-query'

import { triggerAureusDeboosting } from '@utils/aureus'
import {
  changePlayerRecommendationsState,
  getJWPlayer,
  setPlayerRecommendationsAvailable,
} from '@utils/videoPlayer'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import {
  getVideoRecommendations,
  isVideoRecommendationsEnabled,
  VideoRecommendationItem,
} from '@widgets/Video/recommendations/utils'
import VideoRecommendationsComponent from '@components/Video/VideoRecommendations'

import { UseVideoPlayerPropsOutput } from '../types'

export type VideoRecommendationsProps = Pick<
  UseVideoPlayerPropsOutput,
  | 'isBlickTV'
  | 'isOverlay'
  | 'widgetId'
  | 'showRecommendationsOverlay'
  | 'onRecommendationsOverlay'
  | 'hasNextPlaylistItem'
> & {
  isInArticle?: boolean
  hasPlaylist?: boolean
}

const VideoRecommendations: FunctionComponent<VideoRecommendationsProps> = ({
  widgetId,
  isOverlay,
  showRecommendationsOverlay,
  isBlickTV,
  onRecommendationsOverlay,
  hasNextPlaylistItem,
}) => {
  const pageMetadata = usePageMetadata()
  const articleId = pageMetadata.id

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const isRecommendationEnabled = isVideoRecommendationsEnabled()

  const { data: videoRecommendations } = useQuery<VideoRecommendationItem[]>({
    queryKey: ['embedded-videos', articleId],
    queryFn: () => getVideoRecommendations(articleId as string),
    enabled:
      !!articleId &&
      hasAureusJsClientScriptInitialized &&
      isRecommendationEnabled,
    gcTime: 0,
  })

  const [hideUntilPlayingStatus, setHideUntilPlayingStatus] = useState(false)

  useEffect(() => {
    if (showRecommendationsOverlay?.showRecommendations) {
      setHideUntilPlayingStatus(false)
    }
  }, [showRecommendationsOverlay?.showRecommendations])

  useEffect(() => {
    if (!isBlickTV) {
      const jwPlayer = getJWPlayer(widgetId)

      if (jwPlayer) {
        setPlayerRecommendationsAvailable(
          jwPlayer,
          !!videoRecommendations && videoRecommendations.length > 0,
        )
      }
    }
  }, [isBlickTV, videoRecommendations, widgetId])

  const onItemClickTracking = useCallback<
    TrackingFnType<{ item: VideoRecommendationItem }>
  >(
    ({ extraData }) => ({
      event: 'select_content',
      placement: 'article-recommendations-video-embedded',
      link_id: extraData.item.link.href,
    }),
    [],
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    ({ pageMetadata }) => ({
      event: 'content_impression',
      placement: 'article-recommendations-video-embedded',
      link_id: pageMetadata.url,
    }),
    [],
  )

  const onImpression = useTracking(onImpressionTracking)
  const onClickItem = useTracking(onItemClickTracking)

  const handleOnClickItem = useCallback(
    (item: VideoRecommendationItem) => {
      triggerAureusDeboosting({
        itemsToDeboost: [
          { offerId: item.aureusOfferId, contentId: item.articleId },
        ],
      })
      onClickItem({ item })
    },
    [onClickItem],
  )

  const handleClickClose = useCallback(() => {
    const jwPlayer = getJWPlayer(widgetId)

    if (jwPlayer) {
      changePlayerRecommendationsState(jwPlayer, 'hidden')
    }
    onRecommendationsOverlay?.({
      showRecommendations: false,
    })
  }, [onRecommendationsOverlay, widgetId])

  const isHidden =
    (hasNextPlaylistItem && showRecommendationsOverlay?.reason === 'end') ||
    !showRecommendationsOverlay?.showRecommendations ||
    hideUntilPlayingStatus

  return !isBlickTV && videoRecommendations?.length ? (
    <VideoRecommendationsComponent
      widgetId={widgetId}
      hidden={isHidden}
      data={videoRecommendations}
      onClickItem={handleOnClickItem}
      onClickClose={handleClickClose}
      onImpression={onImpression}
      isFullScreen={isOverlay}
    />
  ) : null
}

const MemoizedVideoRecommendations = memo(VideoRecommendations)

// it's important here to set the displayName in memo, because it shows better on React Devtools
MemoizedVideoRecommendations.displayName = 'MemoizedVideoRecommendations'

export default MemoizedVideoRecommendations
